import React, { useRef } from 'react';
import Icon from '../Icon';
import Colors from '~/constants/colors';

interface ImageUploadProps {
    onFileChange: (files: File[]) => void;
    onBase64Change?: (base64Data: { base64: string; fileName: string }[]) => void;
    returnBase64?: boolean;
    accept?: string;
    multiple?: boolean;
    disabled?: boolean;
    className?: string;
    containerStyle?: React.CSSProperties;
    children?: React.ReactNode;
}

const ImageUpload: React.FC<ImageUploadProps> = ({
    onFileChange,
    onBase64Change,
    returnBase64 = false,
    accept = 'image/png,image/jpg,image/jpeg,image/webp',
    multiple = false,
    disabled = false,
    className = '',
    containerStyle,
    children,
}) => {
    const inputRef = useRef<HTMLInputElement>(null);

    // Helper function to convert file to base64
    const fileToBase64 = (file: File): Promise<string> => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = () => resolve(reader.result as string);
            reader.onerror = () => reject(new Error('Failed to read file'));
        });
    };

    // Helper function to process files and convert to base64 if needed
    const processFiles = async (files: File[]) => {
        // Always call onFileChange with the files
        onFileChange(files);

        // If base64 conversion is requested and callback is provided
        if (returnBase64 && onBase64Change) {
            try {
                const base64Promises = files.map(async (file) => {
                    const base64 = await fileToBase64(file);
                    return {
                        base64,
                        fileName: file.name
                    };
                });
                const base64Data = await Promise.all(base64Promises);

                // Call the base64 callback
                onBase64Change(base64Data);
            } catch (error) {
                console.error('Error converting files to base64:', error);
            }
        }
    };

    const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const files = event.target.files;
        if (files && files.length > 0) {
            await processFiles(Array.from(files));
        }
        // Reset input value to allow selecting the same file again
        if (inputRef.current) {
            inputRef.current.value = '';
        }
    };

    const handleClick = () => {
        if (!disabled) {
            inputRef.current?.click();
        }
    };

    const handleDragOver = (event: React.DragEvent) => {
        event.preventDefault();
        event.stopPropagation();
    };

    const handleDrop = async (event: React.DragEvent) => {
        event.preventDefault();
        event.stopPropagation();

        if (disabled) return;

        const files = event.dataTransfer.files;
        if (files && files.length > 0) {
            const imageFiles = Array.from(files).filter(file =>
                file.type.startsWith('image/')
            );
            if (imageFiles.length > 0) {
                await processFiles(imageFiles);
            }
        }
    };

    return (
        <div
            className={`cursor-pointer ${disabled ? 'opacity-50 cursor-not-allowed' : ''} ${className}`}
            style={containerStyle}
            onClick={handleClick}
            onDragOver={handleDragOver}
            onDrop={handleDrop}
        >
            <input
                ref={inputRef}
                type="file"
                accept={accept}
                multiple={multiple}
                onChange={handleFileSelect}
                className="hidden"
                disabled={disabled}
            />
            {children || (
                <div className="flex flex-col items-center justify-center h-full">
                    <Icon name="gallery-add" color={Colors.GREY_700} size={24} />
                    <span className="text-sm text-gray-500 mt-2">
                        {multiple ? 'Upload images' : 'Upload image'}
                    </span>
                </div>
            )}
        </div>
    );
};

export default ImageUpload;
